import { Link } from 'react-router-dom';
import {
  IconMail,
  IconPhone,
  IconUser,
} from '../../components/icons';
import { DetailRow, PanelHeader, QuickAction, panelClassName } from '../../components/ui';
import SpApiSettings from '../auth/SpApiSettings';
import { adminNavItems } from './adminNav';
import { filterNavItemsByModules, isWorkspaceUser } from './moduleAccess';
import SalesOverviewPanel from './SalesOverviewPanel';

const quickActionCopy = {
  search: 'Find a HAWB, order ID, tracking, or batch and open the full batch',
  products: 'Amazon seller listings with images and product links',
  'upload-tracking':
    'Paste Amazon Business tracking onto procurement lines by US Order ID and ASIN',
  'revenue-calculator': 'Estimate Amazon fees and net proceeds for FBM orders',
  analytics: 'Operational KPIs across orders, procurement, and inventory',
  orders: 'Sync and manage Amazon US & IN order history',
  unshipped: 'Open orders waiting to ship and fulfill',
  procurement: 'Purchase batches linked to order lines',
  fulfillment: 'Post-procurement queues after batches are received',
  'customer-service': 'Follow up on delivered orders and confirmations',
  inventory: 'Stock levels, supplier links, and reorder thresholds',
  invoices: 'Generate and print invoice records',
  reports: 'Operational reports across orders, procurement, and inventory',
};

const onboardingSteps = [
  {
    title: 'Sync orders',
    description: 'Pull US and IN sandbox orders from the Orders section.',
    to: '/admin/orders',
    module: 'orders',
  },
  {
    title: 'Procure & track',
    description: 'Send order lines to procurement and assign tracking numbers.',
    to: '/admin/procurement',
    module: 'procurement',
  },
  {
    title: 'Invoice orders',
    description: 'Generate and print invoice records.',
    to: '/admin/invoices',
    module: 'invoices',
  },
];

function AdminHomePage({ user, token }) {
  const isStaff = isWorkspaceUser(user);
  const allowedNav = filterNavItemsByModules(adminNavItems, user);
  const quickLinks = allowedNav
    .filter((item) => item.id !== 'home')
    .map((item) => ({
      to: item.to,
      icon: item.icon,
      title: item.label,
      description: quickActionCopy[item.id] || item.label,
    }));
  const visibleOnboarding = onboardingSteps.filter((step) =>
    allowedNav.some((item) => item.id === step.module)
  );

  return (
    <section className="grid gap-6">
      <SalesOverviewPanel token={token} />

      {quickLinks.length ? (
        <article className={panelClassName}>
          <PanelHeader
            title="Quick actions"
            subtitle="Open any workspace section you can access — same destinations as the sidebar."
          />
          <div className="grid gap-3 p-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {quickLinks.map((link) => (
              <Link key={link.to} to={link.to} className="block h-full no-underline">
                <QuickAction
                  icon={link.icon}
                  title={link.title}
                  description={link.description}
                />
              </Link>
            ))}
          </div>
        </article>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[1fr_1.5fr]">
        <article className={panelClassName}>
          <PanelHeader title="Account details" subtitle="Your workspace profile information." />
          <dl className="grid gap-5 px-5 py-5">
            <DetailRow icon={IconUser} label="Full name" value={user.name || 'Not provided'} />
            <DetailRow icon={IconMail} label="Email" value={user.email} />
            <DetailRow icon={IconPhone} label="Phone" value={user.phone || 'Not provided'} />
          </dl>
        </article>

        {!isStaff && visibleOnboarding.length ? (
          <article className={panelClassName}>
            <PanelHeader
              title="Getting started"
              subtitle="Follow these steps to set up your seller operations workflow."
            />
            <div className="grid gap-3 p-5 sm:grid-cols-3">
              {visibleOnboarding.map((step, index) => (
                <Link key={step.title} to={step.to} className="no-underline">
                  <div className="crm-step-item h-full transition hover:border-[rgb(var(--crm-accent)/0.35)] hover:shadow-sm">
                    <span className="crm-step-number">{index + 1}</span>
                    <div className="min-w-0">
                      <strong className="block text-sm font-semibold">{step.title}</strong>
                      <p className="crm-text-muted mt-1 text-xs leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </article>
        ) : (
          <article className={panelClassName}>
            <PanelHeader
              title="Your access"
              subtitle="Sections assigned to this staff login."
            />
            <div className="px-5 py-5 text-sm text-[rgb(var(--crm-text-muted))]">
              {allowedNav.length
                ? allowedNav.map((item) => item.label).join(' · ')
                : 'No sections assigned yet.'}
            </div>
          </article>
        )}
      </div>
    </section>
  );
}

export function AdminSettingsPage({ user, token, onUserUpdate }) {
  return (
    <section className="grid gap-5">
      <SpApiSettings user={user} token={token} onUserUpdate={onUserUpdate} />
    </section>
  );
}

export default AdminHomePage;
